import { skillIconMap } from "@constants/common";
import { getCompositeScoreAndLevel } from "@features/report/helpers/points-normalization";
import type { Skill } from "@features/report/helpers/points-normalization";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import { HeaderCardAvatar, MasteryListItem, useDevice } from "@memorang/ui";
import pluralize from "pluralize";
import { useTranslation } from "react-i18next";
import { Card, Divider } from "react-native-paper";
/* ----------------- Types --------------- */
import type { SectionItems } from "../../types";
import CardSkeleton from "../Skeletons/CardSkeleton";
import { ProethicaTopicsSection } from "./ProethicaTopicsSection";

interface Props {
	items: SectionItems;
	showStudyPackViews?: boolean;
	handleSelectTopic: (id: string) => void;
	handleShowPurchase: () => void;
	studyPackSubtitle?: string;
	handleNavigateToDetailView?: () => void;
	loading?: boolean;
	viewMode?: "list" | "grid";
}

const TopicsSection = ({
	items,
	showStudyPackViews,
	handleSelectTopic,
	handleShowPurchase,
	studyPackSubtitle,
	loading,
	viewMode,
}: Props) => {
	const theme = useAppTheme();

	const { isMobile } = useDevice();
	const { t } = useTranslation();
	const demoExamId = useDemoExamStore((state) => state.currentExam.id);
	const usesModuleListLayout = demoExamId === "proethica";

	const CardHeader = () => {
		return (
			<>
				<Card.Title
					title="Study-Pack"
					left={() => {
						return <HeaderCardAvatar icon="tag-multiple-outline" />;
					}}
					subtitle={showStudyPackViews && studyPackSubtitle}
				/>
				<Divider
					style={{
						backgroundColor: theme.colors.surfaceVariant,
					}}
				/>
			</>
		);
	};

	const showCardHeader = false;
	if (loading) {
		return <CardSkeleton />;
	}

	if (usesModuleListLayout) {
		return (
			<ProethicaTopicsSection
				items={items}
				handleShowPurchase={handleShowPurchase}
				viewMode={viewMode}
			/>
		);
	}

	return (
		<Card
			style={{
				borderRadius: isMobile ? 0 : 8,
				overflow: "hidden",
			}}
		>
			{showCardHeader && <CardHeader />}
			{items.map((item) => {
				if ("distribution" in item) {
					const { title, distribution, id, locked, numItems } = item;
					const proficiencyLevel = getCompositeScoreAndLevel(
						[
							{
								skill: item.label as Skill,
								numPointsEarned: item.numPointsEarned || 0,
								numTotalPoints: item.numTotalPoints || 0,
							},
						],
						t,
						theme,
					);
					const colorScale = [
						proficiencyLevel.compositeColor!,
						theme.colors.progress.none,
					];
					return (
						<MasteryListItem
							title={title}
							key={id}
							distribution={distribution}
							rightText={`${numItems} ${pluralize("item", numItems)}`}
							locked={locked}
							variant="large"
							score={proficiencyLevel.compositeScaledScore}
							colorScale={colorScale}
							icon={skillIconMap[title]}
							onPress={
								showStudyPackViews
									? () => {
											if (locked) {
												handleShowPurchase();
											} else {
												handleSelectTopic(id);
											}
										}
									: undefined
							}
						/>
					);
				}
				return null;
			})}
			{/* <Card.Actions>
				<Button onPress={handleNavigateToDetailView} mode="text">
					View All
				</Button>
			</Card.Actions> */}
		</Card>
	);
};

export default TopicsSection;
